<template>
  <div class="space-y-6">
    <h2 class="text-3xl font-bold text-gray-900">Function Test</h2>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Left Column: Test Configuration -->
      <div class="space-y-6">

    <div class="card" v-if="config">
      <h3 class="text-lg font-semibold text-gray-900 mb-3">Configuration</h3>
      <div class="space-y-2 text-sm">
        <p><span class="font-medium">Gatekeeper URL:</span> {{ config.gatekeeperFunctionUrl }}</p>
        <p><span class="font-medium">Environment:</span> {{ config.environment }}</p>
      </div>
    </div>

    <div class="card">
      <h3 class="text-lg font-semibold text-gray-900 mb-4">Test Request</h3>

      <form @submit.prevent="testFunction" class="space-y-4">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Target API</label>
            <select v-model="selectedApi" class="form-select">
              <option value="gatekeeper">Gatekeeper</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">API Key</label>
            <select v-model="testRequest.apiKey" class="form-select">
              <option value="">Select API Key</option>
              <option v-for="apiKey in apiKeys" :key="apiKey.id" :value="apiKey.id">
                {{ apiKey.id }} ({{ apiKey.plan }})
              </option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Method</label>
            <select v-model="testRequest.method" class="form-select">
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="DELETE">DELETE</option>
            </select>
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Path</label>
          <input v-model="testRequest.path" type="text" placeholder="/gate or /api/search" class="form-input" />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Query Parameters</label>
          <div class="border border-gray-300 rounded-md p-4 space-y-2">
            <div v-for="(param, index) in queryParams" :key="index" class="flex space-x-2">
              <input v-model="param.key" placeholder="Key" class="form-input flex-1" />
              <input v-model="param.value" placeholder="Value" class="form-input flex-1" />
              <button @click="removeQueryParam(index)" type="button" class="btn btn-danger btn-sm">×</button>
            </div>
            <button @click="addQueryParam" type="button" class="btn btn-secondary btn-sm">Add Parameter</button>
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Headers</label>
          <div class="border border-gray-300 rounded-md p-4 space-y-2">
            <div v-for="(header, index) in headers" :key="index" class="flex space-x-2">
              <input v-model="header.key" placeholder="Header Name" class="form-input flex-1" />
              <input v-model="header.value" placeholder="Header Value" class="form-input flex-1" />
              <button @click="removeHeader(index)" type="button" class="btn btn-danger btn-sm">×</button>
            </div>
            <button @click="addHeader" type="button" class="btn btn-secondary btn-sm">Add Header</button>
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Request Body</label>
          <textarea v-model="testRequest.requestBody" rows="6" placeholder='{"message": "test"}' class="form-textarea"></textarea>
        </div>

        <div>
          <button type="submit" class="btn btn-primary" :disabled="testing">
            {{ testing ? 'Testing...' : 'Test API' }}
          </button>
        </div>
      </form>
    </div>

    <!-- Search Test Configuration -->
    <div class="card">
      <h3 class="text-lg font-semibold text-gray-900 mb-4">Quick Search Tests</h3>

      <div class="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
        <p class="text-sm text-blue-800">
          <strong>Test URL Format:</strong>
          <code class="bg-blue-100 px-1 rounded">{{ config?.gatekeeperFunctionUrl }}/search?query={{ searchQuery }}&language={{ searchLanguage }}</code>
        </p>
        <p class="text-xs text-blue-600 mt-1">
          This will test the Gatekeeper proxy endpoint with your selected API key in the x-api-key header.
        </p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label for="searchQuery" class="block text-sm font-medium text-gray-700 mb-1">Search Query</label>
          <input
            id="searchQuery"
            v-model="searchQuery"
            type="text"
            class="form-input w-full"
            placeholder="e.g., tandenborstel, nail polish"
          >
        </div>
        <div>
          <label for="searchLanguage" class="block text-sm font-medium text-gray-700 mb-1">Language</label>
          <select id="searchLanguage" v-model="searchLanguage" class="form-select w-full">
            <option value="en-EN">English</option>
            <option value="nl-NL">Dutch</option>
            <option value="fr-FR">French</option>
            <option value="de-DE">German</option>
          </select>
        </div>
      </div>

      <div class="mb-4">
        <button @click="testSearchWithCustomQuery" type="button" class="btn btn-primary" :disabled="testing">
          Test Search: {{ searchQuery || 'Enter query above' }}
        </button>
      </div>

      <div class="border-t pt-4">
        <p class="text-sm text-gray-600 mb-3">Quick test buttons for common searches:</p>
        <div class="flex gap-2 flex-wrap">
          <button @click="testSearchWithQuery('tandenborstel')" type="button" class="btn btn-outline-primary btn-sm" :disabled="testing">
            tandenborstel
          </button>
          <button @click="testSearchWithQuery('nail polish')" type="button" class="btn btn-outline-primary btn-sm" :disabled="testing">
            nail polish
          </button>
          <button @click="testSearchWithQuery('shampoo')" type="button" class="btn btn-outline-primary btn-sm" :disabled="testing">
            shampoo
          </button>
          <button @click="testSearchWithQuery('lipstick')" type="button" class="btn btn-outline-primary btn-sm" :disabled="testing">
            lipstick
          </button>
          <button @click="testSearchWithQuery('mascara')" type="button" class="btn btn-outline-primary btn-sm" :disabled="testing">
            mascara
          </button>
        </div>
      </div>
    </div>

    </div>

    <!-- Right Column: Test Response -->
    <div class="space-y-6">
      <div v-if="testResponse" class="card">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">Test Results</h3>

        <!-- Sent Request -->
        <div class="mb-6">
          <h4 class="text-md font-semibold text-gray-900 mb-2">Sent Request</h4>
          <div class="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div class="text-sm text-blue-800 mb-2">
              <strong>{{ sentRequest?.method || 'GET' }} {{ sentRequest?.url }}</strong>
            </div>
            <div v-if="sentRequest?.headers && Object.keys(sentRequest.headers).length > 0" class="mb-2">
              <div class="text-xs text-blue-600 mb-1">Headers:</div>
              <pre class="text-xs text-blue-700 whitespace-pre-wrap">{{ JSON.stringify(sentRequest.headers, null, 2) }}</pre>
            </div>
            <div v-if="sentRequest?.body" class="mb-2">
              <div class="text-xs text-blue-600 mb-1">Body:</div>
              <pre class="text-xs text-blue-700 whitespace-pre-wrap">{{ formatJson(sentRequest.body) }}</pre>
            </div>
          </div>
        </div>

        <!-- Response -->
        <h4 class="text-md font-semibold text-gray-900 mb-2">Response</h4>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 bg-gray-50 rounded-md">
        <div>
          <span class="text-sm font-medium text-gray-500">Status:</span>
          <div class="text-lg font-semibold" :class="testResponse.success ? 'text-green-600' : 'text-red-600'">
            {{ testResponse.statusCode }} {{ testResponse.statusText }}
          </div>
        </div>
        <div>
          <span class="text-sm font-medium text-gray-500">Time:</span>
          <div class="text-lg font-semibold text-gray-900">{{ testResponse.elapsedMs }}ms</div>
        </div>
        <div>
          <span class="text-sm font-medium text-gray-500">Timestamp:</span>
          <div class="text-lg font-semibold text-gray-900">{{ formatDate(testResponse.timestamp) }}</div>
        </div>
      </div>

      <div v-if="testResponse.errorMessage" class="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
        <div class="text-sm font-medium text-red-800">Error:</div>
        <div class="text-sm text-red-700">{{ testResponse.errorMessage }}</div>
      </div>

      <div class="space-y-4">
        <div>
          <h4 class="text-md font-semibold text-gray-900 mb-2">Response Headers</h4>
          <div class="bg-gray-50 rounded-md p-4 space-y-1">
            <div v-for="(value, key) in testResponse.responseHeaders" :key="key" class="text-sm">
              <span class="font-medium text-gray-700">{{ key }}:</span> {{ value }}
            </div>
            <div v-if="Object.keys(testResponse.responseHeaders).length === 0" class="text-sm text-gray-500">
              No headers
            </div>
          </div>
        </div>

        <div>
          <h4 class="text-md font-semibold text-gray-900 mb-2">Response Body</h4>
          <pre class="bg-gray-50 rounded-md p-4 text-sm overflow-x-auto whitespace-pre-wrap">{{ formatJson(testResponse.responseBody) || 'No response body' }}</pre>
        </div>
      </div>
    </div>

    </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { adminApi } from '@/services/api'
import type {
  ApiKey,
  TestConfiguration,
  FunctionTestRequest,
  FunctionTestResponse,
  QuickTest,
  ProxyTest,
  BKSearchTest
} from '@/types'

const config = ref<TestConfiguration | null>(null)
const apiKeys = ref<ApiKey[]>([])
const testing = ref(false)
const testResponse = ref<FunctionTestResponse | null>(null)

const testRequest = ref<FunctionTestRequest>({
  apiKey: '',
  endpoint: 'proxy',
  method: 'GET',
  path: '/search',
  requestBody: '',
  headers: {},
  queryParameters: {
    query: 'tandenborstel',
    language: 'en-EN'
  }
})

const headers = ref<Array<{key: string, value: string}>>([])
const queryParams = ref<Array<{key: string, value: string}>>([
  { key: 'query', value: 'tandenborstel' },
  { key: 'language', value: 'en-EN' }
])

// Search test configuration
const searchQuery = ref('tandenborstel')
const searchLanguage = ref('en-EN')

// API selection
const selectedApi = ref('gatekeeper')

// Sent request tracking
const sentRequest = ref<{
  method: string
  url: string
  headers?: Record<string, string>
  body?: string
} | null>(null)

const loadConfig = async () => {
  try {
    config.value = await adminApi.getTestConfiguration()
  } catch (err) {
    console.error('Failed to load test configuration:', err)
  }
}

const loadApiKeys = async () => {
  try {
    apiKeys.value = await adminApi.getApiKeys()
    // Auto-select the first active API key for convenience
    if (apiKeys.value.length > 0 && !testRequest.value.apiKey) {
      const activeKey = apiKeys.value.find(key => key.status === 1) || apiKeys.value[0]
      testRequest.value.apiKey = activeKey.id
    }
  } catch (err) {
    console.error('Failed to load API keys:', err)
  }
}

const addHeader = () => {
  headers.value.push({ key: '', value: '' })
}

const removeHeader = (index: number) => {
  headers.value.splice(index, 1)
}

const addQueryParam = () => {
  queryParams.value.push({ key: '', value: '' })
}

const removeQueryParam = (index: number) => {
  queryParams.value.splice(index, 1)
}

const buildHeaders = () => {
  const result: Record<string, string> = {}
  headers.value.forEach(header => {
    if (header.key && header.value) {
      result[header.key] = header.value
    }
  })
  return result
}

const buildQueryParams = () => {
  const result: Record<string, string> = {}
  queryParams.value.forEach(param => {
    if (param.key && param.value) {
      result[param.key] = param.value
    }
  })
  return result
}

const testFunction = async () => {
  if (selectedApi.value === 'gatekeeper' && !testRequest.value.apiKey) {
    alert('Please select an API key for Gatekeeper')
    return
  }

  testing.value = true
  testResponse.value = null

  try {
    if (selectedApi.value === 'gatekeeper') {
      // Test Gatekeeper (proxy)
      const request: ProxyTest = {
        apiKey: testRequest.value.apiKey,
        method: testRequest.value.method,
        path: testRequest.value.path,
        requestBody: testRequest.value.requestBody,
        queryParameters: buildQueryParams()
      }

      // Capture sent request details
      const queryString = new URLSearchParams(request.queryParameters).toString()
      sentRequest.value = {
        method: request.method,
        url: `${config.value?.gatekeeperFunctionUrl}${request.path}${queryString ? '?' + queryString : ''}`,
        headers: {
          'x-api-key': request.apiKey,
          'Content-Type': 'application/json'
        },
        body: request.requestBody || undefined
      }

      testResponse.value = await adminApi.testProxyEndpoint(request)
    } else {
      // Test BKSearch directly
      const request: BKSearchTest = {
        query: searchQuery.value,
        language: searchLanguage.value
      }

      // Capture sent request details
      sentRequest.value = {
        method: 'POST',
        url: `${config.value?.bkSearchFunctionUrl}api/search`,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(request)
      }

      testResponse.value = await adminApi.testBKSearchDirect(request)
    }
  } catch (err: any) {
    console.error('Test failed:', err)
    testResponse.value = {
      success: false,
      statusCode: 0,
      statusText: 'Request Failed',
      responseHeaders: {},
      responseBody: '',
      elapsedMs: 0,
      errorMessage: err.message,
      timestamp: new Date().toISOString()
    }
  } finally {
    testing.value = false
  }
}

const testGate = async () => {
  if (!testRequest.value.apiKey) {
    alert('Please select an API key')
    return
  }
  
  testing.value = true
  testResponse.value = null
  
  try {
    const request: QuickTest = {
      apiKey: testRequest.value.apiKey,
      requestBody: testRequest.value.requestBody
    }
    
    testResponse.value = await adminApi.testGateEndpoint(request)
  } catch (err: any) {
    console.error('Gate test failed:', err)
  } finally {
    testing.value = false
  }
}

const testProxy = async () => {
  if (!testRequest.value.apiKey) {
    alert('Please select an API key')
    return
  }
  
  testing.value = true
  testResponse.value = null
  
  try {
    const request: ProxyTest = {
      apiKey: testRequest.value.apiKey,
      method: testRequest.value.method,
      path: testRequest.value.path,
      requestBody: testRequest.value.requestBody,
      queryParameters: buildQueryParams()
    }
    
    testResponse.value = await adminApi.testProxyEndpoint(request)
  } catch (err: any) {
    console.error('Proxy test failed:', err)
  } finally {
    testing.value = false
  }
}

const testBKSearchDirect = async () => {
  testing.value = true
  testResponse.value = null

  try {
    // Extract query from current request body or use default
    let query = 'nail polish'
    try {
      const bodyObj = JSON.parse(testRequest.value.requestBody)
      if (bodyObj.query) query = bodyObj.query
    } catch {
      // Use query parameter if available
      const queryParam = queryParams.value.find(p => p.key === 'query')
      if (queryParam?.value) query = queryParam.value
    }

    const request: BKSearchTest = {
      query: query,
      language: searchLanguage.value
    }

    testResponse.value = await adminApi.testBKSearchDirect(request)
  } catch (err: any) {
    console.error('BKSearch direct test failed:', err)
    testResponse.value = {
      success: false,
      statusCode: 0,
      statusText: 'Request Failed',
      responseHeaders: {},
      responseBody: '',
      elapsedMs: 0,
      errorMessage: err.message,
      timestamp: new Date().toISOString()
    }
  } finally {
    testing.value = false
  }
}

// Search test methods
const testSearchWithQuery = async (query: string) => {
  searchQuery.value = query
  await testSearchWithCustomQuery()
}

const testSearchWithCustomQuery = async () => {
  testing.value = true
  testResponse.value = null

  try {
    if (!testRequest.value.apiKey) {
      throw new Error('Please select an API key first')
    }

    const request: ProxyTest = {
      apiKey: testRequest.value.apiKey,
      method: 'GET',
      path: '/search',
      queryParameters: {
        query: searchQuery.value,
        language: searchLanguage.value
      }
    }

    testResponse.value = await adminApi.testProxyEndpoint(request)
  } catch (err: any) {
    console.error('Search test failed:', err)
    testResponse.value = {
      success: false,
      statusCode: 0,
      statusText: 'Request Failed',
      responseHeaders: {},
      responseBody: '',
      elapsedMs: 0,
      errorMessage: err.message,
      timestamp: new Date().toISOString()
    }
  } finally {
    testing.value = false
  }
}

const quickSearchTest = async () => {
  await testSearchWithCustomQuery()
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString()
}

const formatJson = (jsonString: string | null | undefined) => {
  if (!jsonString) return ''
  try {
    const parsed = JSON.parse(jsonString)
    return JSON.stringify(parsed, null, 2)
  } catch {
    return jsonString
  }
}

onMounted(() => {
  loadConfig()
  loadApiKeys()
})
</script>


